import React, { useState } from 'react'
import { COLORS } from '../theme'

// Where the heads went: each team's seats split by the kind of work they
// finished, in the words on Marcus's 15 Oct page.
//
// Team rows are SHARES and the org row is HEADS, deliberately; small teams are
// combined and Argus is a line of its own (heads-split.js on the server says
// why). Keeping the lights on is one slice until it is clicked, then its two
// halves, support and platform.

const NOT_PLANNED = '#f87171'
const PLATFORM = '#fcd34d'
const SLICES = [
    { key: 'bets', label: 'Roadmap bets', short: 'Bets', color: COLORS.primary },
    { key: 'ktlo', label: 'Keeping the lights on', short: 'Lights on', color: COLORS.warning, parts: ['support', 'platform'] },
    { key: 'unplanned', label: 'Not in the plan', short: 'Not planned', color: NOT_PLANNED },
]
const PARTS = {
    support: { label: 'Support', short: 'Support', color: COLORS.warning },
    platform: { label: 'Platform', short: 'Platform', color: PLATFORM },
}

/**
 * The slices to draw, with keeping-the-lights-on whole or in its two halves.
 * A whole KTLO uses `values.ktlo` when the source sent one (a share taken
 * before rounding), else the sum of its halves (heads). Pure.
 */
export function slicesOf(values, openKtlo) {
    const out = []
    for (const s of SLICES) {
        if (s.parts && openKtlo) {
            for (const p of s.parts) out.push({ key: p, ...PARTS[p], value: values[p] || 0 })
        } else if (s.parts) {
            const whole = typeof values.ktlo === 'number' ? values.ktlo : s.parts.reduce((n, p) => n + (values[p] || 0), 0)
            out.push({ key: s.key, label: s.label, short: s.short, color: s.color, value: whole })
        } else {
            out.push({ key: s.key, label: s.label, short: s.short, color: s.color, value: values[s.key] || 0 })
        }
    }
    return out
}

const fmt = (x) => (Math.round(x * 10) / 10).toFixed(1).replace(/\.0$/, '')

// A change in points of share: '+5', '-3', '0', written as People, per team
// writes its change. No colour: whether more keeping-the-lights-on is good
// depends on the quarter, and the board does not say which way is better
// where it cannot know.
const signed = (d) => (d > 0 ? `+${d}` : String(d))
// '2026 Q3' → 'Q3'.
const shortQ = (label) => String(label || '').replace(/^\d{4} /, '')

function Toggle({ options, value, onChange, label }) {
    return (
        <div role="group" aria-label={label} style={{ display: 'inline-flex' }}>
            {options.map(([key, text], i) => {
                const on = key === value
                return (
                    <button
                        key={key}
                        aria-pressed={on}
                        onClick={() => onChange(key)}
                        style={{
                            background: on ? COLORS.primary : 'transparent',
                            border: `1px solid ${on ? COLORS.primary : COLORS.border}`,
                            borderLeftWidth: i === 0 || on ? 1 : 0,
                            borderRadius: i === 0 ? '6px 0 0 6px' : i === options.length - 1 ? '0 6px 6px 0' : 0,
                            color: on ? '#fff' : COLORS.textMuted,
                            padding: '4px 12px',
                            fontSize: 12,
                            cursor: 'pointer',
                        }}
                    >
                        {text}
                    </button>
                )
            })}
        </div>
    )
}

function Bar({ slices }) {
    const total = slices.reduce((n, s) => n + s.value, 0) || 1
    return (
        <div style={{ display: 'flex', height: 14, borderRadius: 4, overflow: 'hidden', background: COLORS.border }}>
            {slices.filter((s) => s.value > 0).map((s) => (
                <div key={s.key} title={`${s.label}: ${s.value}%`} style={{ width: `${(100 * s.value) / total}%`, background: s.color }} />
            ))}
        </div>
    )
}

function Figure({ s, onClick, open, change, since }) {
    const body = (
        <>
            <div style={{ fontSize: 26, fontWeight: 700, color: COLORS.text, lineHeight: 1.1 }}>{fmt(s.value)}</div>
            {change != null && (
                <div style={{ fontSize: 11.5, color: COLORS.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                    {signed(change)} pts vs {shortQ(since)}
                </div>
            )}
            <div style={{ fontSize: 12, color: COLORS.textMuted, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 9, height: 9, borderRadius: 2, background: s.color, display: 'inline-block' }} aria-hidden="true" />
                {s.label}
                {onClick ? (open ? ' ▾' : ' ▸') : ''}
            </div>
        </>
    )
    if (!onClick) return <div>{body}</div>
    return (
        <button
            onClick={onClick}
            title={open ? 'Show keeping the lights on as one' : 'Split into support and platform'}
            style={{ background: 'transparent', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer' }}
        >
            {body}
        </button>
    )
}

export default function HeadsPanel({ value, note }) {
    const [win, setWin] = useState('quarter')
    const [by, setBy] = useState('points')
    const [openKtlo, setOpenKtlo] = useState(false)
    // Closed quarters, oldest first, then this quarter and the rolling 13 weeks.
    const closed = Array.isArray(value?.quarters) ? value.quarters.filter((q) => q && q.id && q.org) : []
    // A closed quarter that leaves the list on a refresh (the oldest drops as a
    // new one closes) falls back to this quarter rather than blanking the panel.
    const w = win === 'quarter' || win === 'rolling' ? value?.[win] : closed.find((q) => q.id === win) || value?.quarter
    if (!w || !w.org) return null
    const toggleKtlo = () => setOpenKtlo((o) => !o)
    const heads = slicesOf(w.org.heads[by] || {}, openKtlo)
    // The change since the quarter before, keyed like the slices (KTLO whole or
    // in halves), or null for a window that has none.
    const change = w.change && w.change.org ? w.change : null
    const deltaOf = (values) => Object.fromEntries(slicesOf(values || {}, openKtlo).map((d) => [d.key, d.value]))
    const orgDelta = change ? deltaOf(change.org[by]) : null

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Toggle
                    label="Window"
                    value={win === 'quarter' || win === 'rolling' || closed.some((q) => q.id === win) ? win : 'quarter'}
                    onChange={setWin}
                    options={[...closed.map((q) => [q.id, q.label]), ['quarter', 'Quarter to date'], ['rolling', 'Last 13 weeks']]}
                />
                <Toggle label="Weighted by" value={by} onChange={setBy} options={[['points', 'By story points'], ['count', 'By count']]} />
            </div>

            {w.caption && <div style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.45 }}>{w.caption}</div>}
            {change && (
                <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.45 }}>
                    Change since {change.since}, the whole quarter, in points of share: each kind's share of the seats split, then against the same share in {change.since}.
                </div>
            )}

            {/* The org, in heads. */}
            <div style={{ fontSize: 11, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Heads, across the {w.org.split} seats split
            </div>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                {heads.map((s) => {
                    const ktlo = s.key === 'ktlo' || s.key === 'support' || s.key === 'platform'
                    return (
                        <Figure
                            key={s.key}
                            s={s}
                            onClick={ktlo ? toggleKtlo : null}
                            open={openKtlo}
                            change={orgDelta ? orgDelta[s.key] : null}
                            since={change?.since}
                        />
                    )
                })}
            </div>

            {/* Each team, as shares of its own split seats. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(96px, 140px) 1fr minmax(0, 360px)', gap: '8px 14px', alignItems: 'center' }}>
                {w.teams.map((t) => {
                    if (t.suppressed) {
                        return (
                            <React.Fragment key={t.team}>
                                <div style={{ fontSize: 13, color: COLORS.text }}>{t.team}</div>
                                <div style={{ fontSize: 12, color: COLORS.textMuted }}>—</div>
                                <div style={{ fontSize: 11.5, color: COLORS.textMuted }}>too few seats to show a split</div>
                            </React.Fragment>
                        )
                    }
                    const sl = slicesOf(t[by] || {}, openKtlo)
                    const td = change && change.teams[t.team] ? deltaOf(change.teams[t.team][by]) : null
                    return (
                        <React.Fragment key={t.team}>
                            <div style={{ fontSize: 13, color: COLORS.text }}>{t.team}</div>
                            <Bar slices={sl} />
                            <div style={{ fontSize: 11.5, color: COLORS.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                                {/* A slice that fell to 0% still shows when it moved: that drop is the trend. */}
                                {sl.filter((s) => s.value > 0 || (td && td[s.key])).map((s) => `${s.short} ${s.value}%${td ? ` (${signed(td[s.key])})` : ''}`).join(' · ')}
                            </div>
                        </React.Fragment>
                    )
                })}
            </div>

            {w.org.mobile && (
                <div style={{ fontSize: 12.5, color: COLORS.text }}>
                    On mobile work: {fmt(w.org.mobile[by] || 0)} heads (shown until the 15 Dec launch).
                </div>
            )}
            {w.footnote && <div style={{ fontSize: 11.5, color: COLORS.textMuted, lineHeight: 1.45 }}>{w.footnote}</div>}
            {note && <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.45, borderTop: `1px dashed ${COLORS.border}`, paddingTop: 8 }}>{note}</div>}
        </div>
    )
}
